import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { MeteoConfig } from '../types';
import { useConfirm } from '../components/ui';
import { CloudSun, FlaskConical, RefreshCw, Info } from 'lucide-react';
import './ImpostazioniMeteoPage.css';

const ImpostazioniMeteoPage: React.FC = () => {
  const { user } = useAuth();
  const confirm = useConfirm();

  const [config, setConfig] = useState<MeteoConfig>({
    apiKey: '',
    aggiornamentoAutomatico: true,
    intervalloOre: 3,
    provider: 'openweathermap'
  });
  
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [testResult, setTestResult] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);

  useEffect(() => {
    if (user?.ruolo !== 'admin') {
      setMessage({type: 'error', text: 'Accesso negato. Solo gli amministratori possono accedere a questa pagina.'});
      return;
    }
    loadConfig();
  }, [user]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const response = await apiService.getWeatherConfig();
      
      console.log('Risposta configurazione meteo:', response);
      
      if (response.success && response.data) {
        setConfig({
          apiKey: response.data['OPENWEATHER_API_KEY'] || '',
          aggiornamentoAutomatico: response.data['meteo_aggiornamento_automatico'] === 'true',
          intervalloOre: parseInt(response.data['meteo_intervallo_ore']) || 3,
          provider: response.data['meteo_provider'] || 'openweathermap'
        });
      }
    } catch (error: any) {
      console.error('Errore caricamento configurazione:', error);
      setMessage({type: 'error', text: 'Errore nel caricamento della configurazione'});
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const result = await apiService.testWeatherConnection();
      setTestResult({
        type: 'success',
        message: `Connessione riuscita. Località test: ${result.location || 'N/A'}, Temperatura: ${result.temperature || 'N/A'}°C`
      });
    } catch (error: any) {
      console.error('Errore test connessione:', error);
      setTestResult({
        type: 'error',
        message: error.message || 'Errore nella connessione'
      });
    } finally {
      setTesting(false);
    }
  };

  const handleUpdateAll = async () => {
    const ok = await confirm({
      title: 'Aggiorna dati meteo',
      message: 'Aggiornare i dati meteo per tutti i punti vendita? Questa operazione potrebbe richiedere alcuni minuti.',
      confirmLabel: 'Aggiorna',
    });
    if (!ok) {
      return;
    }

    try {
      setUpdating(true);
      await apiService.updateAllWeather();
      setMessage({type: 'success', text: 'Aggiornamento dati meteo completato per tutti i punti vendita'});
    } catch (error: any) {
      console.error('Errore aggiornamento dati:', error);
      setMessage({type: 'error', text: error.message || 'Errore nell\'aggiornamento dei dati meteo'});
    } finally {
      setUpdating(false);
    }
  };

  if (user?.ruolo !== 'admin') {
    return (
      <div className="page-container">
        <div className="error-message">
          <span>Accesso negato. Solo gli amministratori possono accedere a questa pagina.</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-message">
        <span>Caricamento configurazioni meteo...</span>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>
          <CloudSun size={20} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
          Configurazione Meteo
        </h1>
        <p>Gestisci le impostazioni per il sistema di monitoraggio meteo</p>
      </div>

      {message && (
        <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
          <span>{message.text}</span>
          <button className="close-btn" onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="meteo-settings-grid">
        <div className="settings-card">
          <div className="card-header">
            <h2>Stato Configurazione API</h2>
          </div>

          <div className="info-section">
            <div className="info-item">
              <span className="info-label">Provider Meteo:</span>
              <span className="info-value">{config.provider || 'OpenWeatherMap'}</span>
            </div>
            
            <div className="info-item">
              <span className="info-label">API Key:</span>
              <span className="info-value">
                {config.apiKey ? (
                  <span className="status-success">Configurata</span>
                ) : (
                  <span className="status-warning">Non configurata</span>
                )}
              </span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Aggiornamento Automatico:</span>
              <span className="info-value">
                {config.aggiornamentoAutomatico ? (
                  <span className="status-success">Attivo</span>
                ) : (
                  <span className="status-inactive">Disattivo</span>
                )}
              </span>
            </div>
            
            <div className="info-item">
              <span className="info-label">Intervallo Aggiornamento:</span>
              <span className="info-value">{config.intervalloOre || 3} ore</span>
            </div>
          </div>

          <div className="admin-notice">
            <p>
              <strong>Nota:</strong> Le configurazioni API vengono gestite dal super amministratore
              per motivi di sicurezza. Per modificare la API key o altre impostazioni sensibili, 
              contattare l'amministratore di sistema.
            </p>
          </div>
        </div>

        {/* Card Test e Azioni */}
        <div className="settings-card">
          <div className="card-header">
            <h3>
              <FlaskConical size={18} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
              Test e Azioni
            </h3>
            <p>Testa la configurazione e gestisci i dati meteo</p>
          </div>
          <div className="card-content">
          <div className="form-group">
            <label>Test Connessione API</label>
            <p className="description">Verifica che la configurazione sia corretta testando la connessione con il servizio meteo.</p>
            <button
              onClick={handleTest}
              disabled={testing || !config.apiKey}
              className="btn-secondary"
            >
              {testing ? 'Test in corso...' : 'Testa Connessione'}
            </button>
            {testResult && (
              <div className={`test-result ${testResult.type}`}>
                <strong>{testResult.type === 'success' ? 'Successo' : 'Errore'}:</strong>
                <p>{testResult.message}</p>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Aggiorna Tutti i Dati</label>
            <p className="description">Forza l'aggiornamento dei dati meteo per tutti i punti vendita configurati.</p>
            <button
              onClick={handleUpdateAll}
              disabled={updating || !config.apiKey}
              className="btn-accent"
            >
              <RefreshCw size={16} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
              {updating ? 'Aggiornamento...' : 'Aggiorna Tutti i Dati'}
            </button>
          </div>
        </div>
      </div>

      {/* Card Informazioni */}
      <div className="settings-card">
        <div className="card-header">
          <h3>
            <Info size={18} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />
            Informazioni Sistema Meteo
          </h3>
          <p>Dettagli su come funziona l'integrazione meteo</p>
        </div>
        <div className="card-content">
          <div className="info-section">
            <h4>Funzionalità</h4>
            <ul>
              <li><strong>Dati attuali:</strong> Temperatura, umidità, condizioni meteo per ogni punto vendita</li>
              <li><strong>Previsioni:</strong> Forecast a 7 giorni per ottimizzare la produzione</li>
              <li><strong>Storico:</strong> Correlazione tra meteo e vendite per analisi avanzate</li>
              <li><strong>Automazione:</strong> Aggiornamento automatico configrabile</li>
            </ul>
          </div>

          <div className="info-section">
            <h4>Configurazione</h4>
            <ul>
              <li><strong>API Key:</strong> Necessaria per accedere ai dati di OpenWeatherMap</li>
              <li><strong>Coordinate:</strong> Vengono utilizzate le coordinate dei punti vendita</li>
              <li><strong>Aggiornamento:</strong> Configurabile da 1 a 24 ore</li>
              <li><strong>Multi-tenant:</strong> Ogni gelateria ha configurazioni indipendenti</li>
            </ul>
          </div>

          <div className="info-section">
            <h4>Utilizzo</h4>
            <ul>
              <li><strong>Dashboard:</strong> Widget meteo per ogni punto vendita</li>
              <li><strong>Produzione:</strong> Informazioni meteo salvate per ogni giornata</li>
              <li><strong>Analytics:</strong> Correlazione meteo-vendite nelle statistiche</li>
              <li><strong>Export:</strong> Dati meteo inclusi negli export Excel</li>
            </ul>
          </div>
        </div>
      </div>

      </div>
    </div>
  );
};

export default ImpostazioniMeteoPage;
